/**
 * Editor Registry and Factory
 *
 * Maps InputType to specialized cell editor components for MUI X Data Grid.
 * Provides buildEditorColumn function to create column definitions with appropriate editors.
 */

import React, { useCallback, useContext } from "react";
import {
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControl,
} from "@mui/material";
import {
  GridRenderEditCellParams,
  GridColDef,
  useGridApiContext,
} from "@mui/x-data-grid";
import { DirectCellUpdateContext } from "../DirectCellUpdateContext";
import { DataGridFieldAdapter } from "./DataGridFieldAdapter";
import { ColumnStatusColorsContext, GridStatusPill } from "../GridCells";
import type { ViewColumn } from "../../../services/viewService";

// Complex editors
import JsonEditor from "./JsonEditor";
import FkReferenceEditor from "./FkReferenceEditor";
import FkSelectEditor from "./FkSelectEditor";
import ArrayEditor from "./ArrayEditor";

/**
 * Input type for determining which editor to use
 */
export type InputType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "datetime"
  | "boolean"
  | "select"
  | "email"
  | "percentage"
  | "currency"
  | "json"
  | "fk_reference"
  | "array"
  | "file";

/**
 * Extended props passed to all editor components
 */
export interface EditorProps extends GridRenderEditCellParams {
  enumOptions?: string[];
  fkReferenceTableId?: string;
  fkReferenceTableName?: string;
  fkDisplayColumn?: string;
  formatHint?: string;
  minValue?: number;
  maxValue?: number;
  nullable?: boolean;
  viewId?: string;
  linkFieldIds?: string[];
  fkColumnKey?: string;
  targetTable?: string;
  targetField?: string;
}

/**
 * Text Editor using DataGridFieldAdapter
 */
function TextEditorAdapter(props: EditorProps) {
  const { nullable } = props;
  return (
    <DataGridFieldAdapter {...props} nullable={nullable}>
      {({ value, onChange, onBlur, autoFocus }) => (
        <TextField
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          autoFocus={autoFocus}
          size="small"
          variant="outlined"
          fullWidth
        />
      )}
    </DataGridFieldAdapter>
  );
}

/**
 * Textarea Editor using DataGridFieldAdapter
 */
function TextareaEditorAdapter(props: EditorProps) {
  const { nullable } = props;
  return (
    <DataGridFieldAdapter {...props} nullable={nullable}>
      {({ value, onChange, onBlur, autoFocus }) => (
        <TextField
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          autoFocus={autoFocus}
          size="small"
          variant="outlined"
          fullWidth
          multiline
        />
      )}
    </DataGridFieldAdapter>
  );
}

/**
 * Number Editor using DataGridFieldAdapter
 */
function NumberEditorAdapter(props: EditorProps) {
  const { nullable } = props;
  return (
    <DataGridFieldAdapter {...props} nullable={nullable}>
      {({ value, onChange, onBlur, autoFocus }) => (
        <TextField
          type="number"
          value={value != null ? String(value) : ""}
          onChange={(e) => {
            const val = e.target.value;
            onChange(val === "" ? null : Number(val));
          }}
          onBlur={onBlur}
          autoFocus={autoFocus}
          size="small"
          variant="outlined"
          fullWidth
        />
      )}
    </DataGridFieldAdapter>
  );
}

/**
 * Select Editor - stops edit mode immediately after selection
 */
function SelectEditorAdapter(props: EditorProps) {
  const { id, field, value, nullable, enumOptions } = props;
  const apiRef = useGridApiContext();
  const statusColors = useContext(ColumnStatusColorsContext);

  const handleChange = useCallback(
    (event: { target: { value: string } }) => {
      const newValue = event.target.value || null;
      apiRef.current.setEditCellValue({ id, field, value: newValue });
      setTimeout(() => {
        apiRef.current.stopCellEditMode({ id, field });
      }, 0);
    },
    [apiRef, id, field],
  );

  return (
    <Select
      value={(value as string) ?? ""}
      onChange={handleChange}
      autoFocus
      size="small"
      variant="outlined"
      fullWidth
      renderValue={
        statusColors
          ? (selected) => (
              <GridStatusPill
                value={String(selected)}
                statusColors={statusColors}
              />
            )
          : undefined
      }
    >
      {nullable && (
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
      )}
      {enumOptions?.map((option) => (
        <MenuItem key={option} value={option}>
          {statusColors ? (
            <GridStatusPill value={option} statusColors={statusColors} />
          ) : (
            option
          )}
        </MenuItem>
      ))}
    </Select>
  );
}

/**
 * Convert value to YYYY-MM-DD string for date input
 */
function toDateInputString(value: unknown): string {
  if (!value) return "";
  if (value instanceof Date) {
    // Format Date as YYYY-MM-DD
    return value.toISOString().split("T")[0];
  }
  // Already a string (e.g., "2024-01-15")
  return String(value);
}

/**
 * Date Editor using DataGridFieldAdapter
 * Converts string input to Date object for the grid, which expects Date values
 */
function DateEditorAdapter(props: EditorProps) {
  const { nullable } = props;
  return (
    <DataGridFieldAdapter {...props} nullable={nullable}>
      {({ value, onChange, onBlur, autoFocus }) => (
        <TextField
          type="date"
          value={toDateInputString(value)}
          onChange={(e) => {
            const inputValue = e.target.value;
            if (!inputValue) {
              onChange(null);
            } else {
              // Convert string to Date object for the DataGrid
              const date = new Date(inputValue + "T00:00:00");
              onChange(date);
            }
          }}
          onBlur={onBlur}
          autoFocus={autoFocus}
          size="small"
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
      )}
    </DataGridFieldAdapter>
  );
}

/**
 * Boolean Editor - Single-click toggle (uses DirectCellUpdateContext)
 */
function BooleanEditorCell(props: EditorProps) {
  const { id, field, value } = props;
  const directCellUpdate = useContext(DirectCellUpdateContext);

  const handleChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;
      if (directCellUpdate) {
        try {
          await directCellUpdate(id, field, newValue);
        } catch (error) {
          console.error("Failed to update boolean cell:", error);
        }
      }
    },
    [directCellUpdate, id, field],
  );

  return (
    <FormControl
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
      }}
    >
      <Switch checked={Boolean(value)} onChange={handleChange} size="small" />
    </FormControl>
  );
}

/**
 * Editor component type
 */
type EditorComponent = React.ComponentType<EditorProps>;

/**
 * Registry mapping InputType to editor components
 */
const editorRegistry: Partial<Record<InputType, EditorComponent>> = {
  boolean: BooleanEditorCell,
  select: SelectEditorAdapter,
  date: DateEditorAdapter,
  datetime: DateEditorAdapter,
  text: TextEditorAdapter,
  textarea: TextareaEditorAdapter,
  number: NumberEditorAdapter,
  email: TextEditorAdapter,
  percentage: NumberEditorAdapter,
  currency: NumberEditorAdapter,
  json: JsonEditor,
  fk_reference: FkReferenceEditor,
  array: ArrayEditor,
  file: TextEditorAdapter, // File columns are non-editable; upload/download via cell renderer
};

/**
 * Get the appropriate editor component for an input type
 */
export function getEditorComponent(inputType: InputType): EditorComponent {
  return editorRegistry[inputType] || TextEditorAdapter;
}

/**
 * Build column definition with appropriate editor for a ViewColumn
 *
 * @param col - The ViewColumn metadata
 * @param viewId - Optional view ID (needed for reference-options endpoint)
 */
export function buildEditorColumn(
  col: ViewColumn,
  viewId?: string,
): Partial<GridColDef> {
  // For REFERENCE mode fields, always use FkReferenceEditor
  if (col.editMode === "REFERENCE") {
    return {
      renderEditCell: (params) => (
        <FkReferenceEditor
          {...params}
          fkReferenceTableId={col.fkReferenceTableId}
          fkReferenceTableName={col.fkReferenceTableName}
          fkDisplayColumn={col.fkDisplayColumn}
          nullable={col.nullable}
          viewId={viewId}
          linkFieldIds={col.linkFieldIds}
          fkColumnKey={col.fkColumnKey}
        />
      ),
    };
  }

  const inputType = (col.inputType || "text") as InputType;
  const EditorComponent = getEditorComponent(inputType);

  // For boolean columns, use single-click toggle via DirectCellUpdateContext
  if (inputType === "boolean") {
    return {
      renderCell: (params) => (
        <BooleanEditorCell {...params} nullable={col.nullable} />
      ),
      editable: false, // We handle saves directly via context
    };
  }

  // For select columns with enum options
  if (inputType === "select" && col.enumOptions && col.enumOptions.length > 0) {
    return {
      renderEditCell: (params) => (
        <SelectEditorAdapter
          {...params}
          enumOptions={col.enumOptions}
          nullable={col.nullable}
        />
      ),
    };
  }

  // For FK fields that aren't link fields, use simple select
  if (
    inputType === "fk_reference" &&
    !col.linkFieldIds?.length &&
    col.fkReferenceTableId
  ) {
    return {
      renderEditCell: (params) => (
        <FkSelectEditor
          {...params}
          fkReferenceTableId={col.fkReferenceTableId}
          nullable={col.nullable}
        />
      ),
    };
  }

  // For other types, use custom renderEditCell
  return {
    renderEditCell: (params) => {
      // For JSON editors, use actual table name from column metadata
      let targetTable: string | undefined;
      let targetField: string | undefined;

      if (inputType === "json") {
        targetTable = col.actualTableName;
        const fieldParts = col.field.split(".");
        targetField =
          fieldParts.length > 1 ? fieldParts.slice(1).join(".") : col.field;
      }

      return (
        <EditorComponent
          {...params}
          enumOptions={col.enumOptions}
          fkReferenceTableId={col.fkReferenceTableId}
          fkReferenceTableName={col.fkReferenceTableName}
          fkDisplayColumn={col.fkDisplayColumn}
          formatHint={col.formatHint}
          minValue={col.minValue}
          maxValue={col.maxValue}
          nullable={col.nullable}
          viewId={viewId}
          linkFieldIds={col.linkFieldIds}
          fkColumnKey={col.fkColumnKey}
          targetTable={targetTable}
          targetField={targetField}
        />
      );
    },
  };
}

// Export individual editors
export {
  DataGridFieldAdapter,
  TextEditorAdapter as TextEditor,
  TextareaEditorAdapter as TextareaEditor,
  NumberEditorAdapter as NumberEditor,
  SelectEditorAdapter as SelectEditor,
  DateEditorAdapter as DateEditor,
  BooleanEditorCell as BooleanEditor,
  JsonEditor,
  FkReferenceEditor,
  FkSelectEditor,
  ArrayEditor,
};

// Legacy exports for backwards compatibility
export const EmailEditor = TextEditorAdapter;
export const PercentageEditor = NumberEditorAdapter;
export const CurrencyEditor = NumberEditorAdapter;
export const DateTimeEditor = DateEditorAdapter;
